import React from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import { MDBBtn, MDBInput, MDBModal, MDBModalDialog, MDBModalContent, MDBModalHeader, MDBModalTitle, MDBModalBody, MDBModalFooter } from 'mdb-react-ui-kit';
import { baseURL } from '../../api/baseURL';
import Loader from '../../Loader';
import Modal from '../../Modal';
import Pagination from '../Pagination';
import ExportButton from '../ExportButton';
import debounce from 'debounce';
import AdminShell from '../layout/AdminShell';
import { countPillStyle, pillStyle, toolbarRowStyle, searchBoxWrapperStyle, thStyle } from '../layout/listUi';
import { isMainAdmin } from '../../utils/isMainAdmin';

type PaymentRow = {
    _id: string,
    order_receipt_id: string,
    photographer_name: string,
    photographer_email: string,
    photographer_contact: string,
    specialization: string,
    category: string,
    subcategory: string,
    service_type: string,
    slot_index: number,
    start_time: string,
    end_time: string,
    calculated_hours: number,
    hours_to_pay: number,
    amount: number,
    // Phase B: computed read-time suggestion from the admin-set service payout
    // rate (Services tab) — never persisted, only present when a rate is
    // configured for this row's service_type. Purely a prefill hint.
    suggested_amount?: number,
    payment_status: 'paid' | 'unpaid',
    payout_mode?: string,
    paid_at?: string,
    locked?: boolean,
};

type State = {
    requested: boolean,
    err: any,
    successPayout: boolean,
    paymentsList: any,
    searchedList: any,
    currentPage: number,
    activeTab: 'unpaid' | 'paid' | 'all',
    payModalOpen: boolean,
    selectedPayment: PaymentRow | null,
    // Kept as strings (not number) so the input can hold an empty string
    // while the admin is mid-edit — a controlled number-typed input bound to
    // a number state can't represent "the field is currently empty" (it
    // collapses "" to 0), which was forcing a pre-filled 0 to snap back
    // after a single backspace. Parsed to Number only where actually used.
    payHours: string,
    payAmount: string,
    paymentMode: string,
    // BUG-025: server-reported flag so admin UI can distinguish live vs mock
    // payouts and gate live payouts behind a typed confirmation.
    payoutTestMode: boolean,
    liveConfirmChecked: boolean,
};

class PaymentList extends React.Component<{ history: any, token: string, role?: string }, State> {
    state: State = {
        requested: false,
        err: false,
        successPayout: false,
        paymentsList: null,
        searchedList: null,
        currentPage: 1,
        activeTab: 'unpaid',
        payModalOpen: false,
        selectedPayment: null,
        payHours: '',
        payAmount: '',
        paymentMode: 'bank',
        payoutTestMode: false,
        liveConfirmChecked: false,
    }

    componentDidMount(): void {
        if (!this.props.token) {
            window.location.pathname = "/";
            return;
        }

        this.getPaymentsList(parseInt(window.location.search.split("?page=")[1]) ? parseInt(window.location.search.split("?page=")[1]) : 1, this.state.activeTab);
    }

    componentDidUpdate(prevProps: Readonly<{ history: any; token: string; }>, prevState: Readonly<State>): void {
        if (prevState.activeTab !== this.state.activeTab) {
            this.getPaymentsList(1, this.state.activeTab);
        }
    }

    getPaymentsList = (pageNumber: number, status: 'unpaid' | 'paid' | 'all', searchText?: string) => {
        if (!this.state.requested) {
            this.setState({ err: false, requested: true });

            const query = searchText?.length ? `&search=${encodeURIComponent(searchText)}` : '';
            axios.get(`${baseURL}/payments?page=${pageNumber}&limit=10&status=${status}${query}`,
                {
                    headers: { Authorization: `Bearer ${this.props.token}` }
                })
                .then((response: any) => {
                    if (response?.data?.message === "success") {
                        this.setState({
                            paymentsList: response?.data,
                            searchedList: searchText?.length ? response?.data : null,
                            requested: false,
                            err: false,
                            currentPage: pageNumber,
                            // BUG-025: reflect backend flag so the modal can render LIVE warning.
                            payoutTestMode: Boolean(response?.data?.payout_test_mode),
                        });
                    }
                    else {
                        this.setState({ err: true, requested: false });
                    }
                })
                .catch(() => {
                    this.setState({ err: true, requested: false });
                })
        }
    }

    onPageChange = (pageNumber: number) => {
        this.getPaymentsList(pageNumber, this.state.activeTab);
    }

    onSearch = debounce((searchText: string) => {
        if (searchText.length > 2) {
            this.getPaymentsList(1, this.state.activeTab, searchText);
        }
    }, 100);

    onClickClear = () => {
        const searchInputEl: any = document.getElementById("search_input");
        if (searchInputEl?.value) {
            searchInputEl.value = "";
        }

        this.setState({ searchedList: null }, () => {
            this.getPaymentsList(1, this.state.activeTab);
        });
    }

    togglePayModal = (payment?: PaymentRow | null) => {
        if (payment) {
            this.setState({
                payModalOpen: true,
                selectedPayment: payment,
                // Pre-fill as a whole hour (round to nearest) rather than the raw
                // fractional shoot duration (e.g. 0.82hr) — still editable before
                // submitting. Floor of 1 whenever there was any real duration, so
                // a short booking (e.g. 0.3hr) doesn't round down to an invalid 0.
                payHours: String((() => {
                    const raw = payment?.hours_to_pay || payment?.calculated_hours || 0;
                    return raw > 0 ? Math.max(1, Math.round(raw)) : 0;
                })()),
                payAmount: String(payment?.amount || payment?.suggested_amount || 0),
                paymentMode: 'bank',
                liveConfirmChecked: false,
            });
            return;
        }

        this.setState({
            payModalOpen: false,
            selectedPayment: null,
            payHours: '',
            payAmount: '',
            paymentMode: 'bank',
            liveConfirmChecked: false,
        });
    }

    toggleModal = () => {
        if (this.state.err) {
            this.setState({ err: false });
        }

        if (this.state.successPayout) {
            this.setState({ successPayout: false });
        }
    }

    submitPayment = () => {
        const selectedPayment = this.state.selectedPayment;
        if (!selectedPayment?._id || !(Number(this.state.payAmount) > 0) || !this.state.paymentMode || this.state.requested) {
            return;
        }

        // BUG-025: on LIVE mode (payout_test_mode=false) require admin to tick
        // the confirmation box — prevents accidental real-money payouts.
        // Client-reported: was a typed "PAY LIVE" text field, replaced with a
        // plain checkbox.
        if (!this.state.payoutTestMode && !this.state.liveConfirmChecked) {
            this.setState({ err: 'live_payout_confirmation_required' });
            return;
        }

        this.setState({ requested: true, err: false, successPayout: false });

        axios.post(`${baseURL}/payout`,
            {
                paymentId: selectedPayment._id,
                email: selectedPayment.photographer_email,
                amount: Number(this.state.payAmount) || 0,
                paymentForHours: Number(this.state.payHours) || 0,
                mode: this.state.paymentMode,
            },
            {
                headers: { Authorization: `Bearer ${this.props.token}` }
            })
            .then((response: any) => {
                if (response?.data?.id) {
                    // Refetch in setState callback: requested is still true until commit, and
                    // getPaymentsList bails when requested === true, so a sync call would skip fetch.
                    this.setState(
                        { requested: false, err: false, successPayout: true, payModalOpen: false, selectedPayment: null },
                        () => this.getPaymentsList(this.state.currentPage, this.state.activeTab)
                    );
                }
                else {
                    this.setState({ requested: false, err: true, successPayout: false });
                }
            })
            .catch((error: any) => {
                const msg = error?.response?.data?.message;
                if (msg === "no_bank_details") {
                    this.setState({ requested: false, err: "no_bank_details", successPayout: false });
                }
                else if (msg === "payment_already_locked") {
                    this.setState(
                        { requested: false, err: "payment_already_locked", successPayout: false, payModalOpen: false },
                        () => this.getPaymentsList(this.state.currentPage, this.state.activeTab)
                    );
                }
                // BUG-026: surface the backend caps to the admin instead of a generic error.
                else if (msg === "hours_over_cap") {
                    this.setState({ requested: false, err: `hours_over_cap_${error?.response?.data?.hours_cap || ''}`, successPayout: false });
                }
                else if (msg === "amount_over_cap") {
                    this.setState({ requested: false, err: `amount_over_cap_${error?.response?.data?.amount_cap || ''}`, successPayout: false });
                }
                else {
                    this.setState({ requested: false, err: true, successPayout: false });
                }
            });
    }

    // Compact "Shoot Time" column: the date printed once plus a start–end
    // time range, instead of two separate full date+time columns repeating
    // the same date — client-reported clutter on the Payments table.
    formatShootTime = (startValue: string, endValue: string) => {
        const start = startValue ? new Date(startValue) : null;
        const end = endValue ? new Date(endValue) : null;
        const startOk = start && !Number.isNaN(start.getTime());
        const endOk = end && !Number.isNaN(end.getTime());
        if (!startOk && !endOk) return "-";

        const dateStr = (startOk ? start : end)!.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        const timeStr = (d: Date) => d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });

        const sameDay = startOk && endOk && start!.toDateString() === end!.toDateString();
        if (startOk && endOk) {
            return sameDay
                ? `${dateStr}, ${timeStr(start!)} – ${timeStr(end!)}`
                : `${dateStr} ${timeStr(start!)} – ${end!.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} ${timeStr(end!)}`;
        }
        return `${dateStr}, ${timeStr((startOk ? start : end)!)}`;
    }

    renderRows = () => {
        const rows = this.state.searchedList?.payments || this.state.paymentsList?.payments || [];

        if (!rows.length) {
            return <div style={{ display: 'flex', justifyContent: 'center', padding: 15, paddingTop: 30 }}>
                <div className="badge badge-outline-secondary" style={{ fontSize: 16 }}>No payment rows</div>
            </div>;
        }

        return <table className="table">
            <thead>
                <tr>
                    <th style={thStyle}>Receipt ID</th>
                    <th style={thStyle}>Photographer</th>
                    <th style={thStyle}>Specialization</th>
                    <th style={thStyle}>Category</th>
                    <th style={thStyle}>Shoot Time</th>
                    <th style={thStyle}>Hours</th>
                    <th style={thStyle}>Amount</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Action</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((item: PaymentRow) => {
                    const effectiveHours = item?.hours_to_pay || item?.calculated_hours || 0;
                    return <tr key={item._id}>
                        <td>{item?.order_receipt_id || "-"}</td>
                        <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                                <span>{item?.photographer_name || "-"}</span>
                                <span style={{ fontSize: 12, color: '#999' }}>{item?.photographer_email || "-"}</span>
                                <span style={{ fontSize: 12, color: '#999' }}>{item?.photographer_contact || "-"}</span>
                            </div>
                        </td>
                        <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                                <span>{item?.specialization || "-"}</span>
                                <span style={{ fontSize: 12, color: '#999' }}>{item?.service_type || "-"} #{item?.slot_index ?? 0}</span>
                            </div>
                        </td>
                        <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                                <span>{item?.category || "-"}</span>
                                <span style={{ fontSize: 12, color: '#999' }}>{item?.subcategory || "-"}</span>
                            </div>
                        </td>
                        <td>{this.formatShootTime(item?.start_time, item?.end_time)}</td>
                        <td>{effectiveHours}</td>
                        <td>{item?.amount ? `₹${item.amount}` : "-"}</td>
                        <td>
                            <span className={`badge ${item?.payment_status === 'paid' ? 'badge-outline-success' : 'badge-outline-warning'}`}>
                                {item?.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                            </span>
                        </td>
                        <td>
                            {item?.payment_status === 'paid' || item?.locked ?
                                <MDBBtn disabled color='secondary'>Locked</MDBBtn> :
                                isMainAdmin(this.props.role) ?
                                    <MDBBtn color='success' onClick={() => { this.togglePayModal(item); }}>Pay</MDBBtn> :
                                    null}
                        </td>
                    </tr>
                })}
            </tbody>
        </table>;
    }

    render() {
        const totalPages = Math.ceil((this.state.paymentsList?.total || 0) / 10);

        return (
            <AdminShell activePage="payments" title="Payments" subtitle="Photographer payouts — pending and paid">
                <Modal
                    open={this.state.err || this.state.successPayout}
                    hideSec
                    primaryText="Okay"
                    title='Update'
                    onConfirm={this.toggleModal}
                    toggleOpen={this.toggleModal}
                    content={<h6>{(() => {
                        if (this.state.successPayout) return "Payment sent successfully";
                        const e = this.state.err;
                        if (e === "no_bank_details") return "There is no bank details added";
                        if (e === "payment_already_locked") return "This slot payment has already been locked as paid";
                        if (e === "live_payout_confirmation_required") return "Tick the confirmation box to authorize a live payout.";
                        if (typeof e === "string" && e.startsWith("hours_over_cap_")) return `Hours exceed the allowed cap (${e.replace("hours_over_cap_", "")}).`;
                        if (typeof e === "string" && e.startsWith("amount_over_cap_")) return `Amount exceeds the allowed cap (₹${e.replace("amount_over_cap_", "")}).`;
                        return "Something went wrong, please try again later";
                    })()}</h6>}
                />
                {this.state.requested ? <Loader /> : null}
                <div className="card">
                    <div className="card-body">
                        <h4 className="card-title">
                            Payments
                            {this.state.paymentsList?.total ? <span style={countPillStyle}>{this.state.paymentsList.total}</span> : null}
                        </h4>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                            <div style={pillStyle(this.state.activeTab === 'all')} onClick={() => this.setState({ activeTab: 'all' })}>All</div>
                            <div style={pillStyle(this.state.activeTab === 'unpaid')} onClick={() => this.setState({ activeTab: 'unpaid' })}>Unpaid</div>
                            <div style={pillStyle(this.state.activeTab === 'paid')} onClick={() => this.setState({ activeTab: 'paid' })}>Paid</div>
                        </div>
                        <div style={toolbarRowStyle}>
                            <div style={searchBoxWrapperStyle}>
                                <MDBInput
                                    onChange={(e) => {
                                        this.onSearch(e.target.value)
                                    }}
                                    contrast label="Search by Receipt / Photographer / Category" id="search_input" type="text" />
                            </div>
                            <MDBBtn onClick={() => { this.onClickClear() }} color='secondary' rippleColor='light'>Clear</MDBBtn>
                            <ExportButton endpoint="/export/payments" />
                        </div>
                        <div className="table-responsive">
                            {this.renderRows()}
                        </div>
                    </div>
                </div>
                {totalPages > 1 ? <Pagination onPageChange={this.onPageChange} currentPage={this.state.currentPage} totalPages={totalPages} maxVisible={5} /> : null}

                <MDBModal open={this.state.payModalOpen} onClose={() => this.togglePayModal()} tabIndex='-1'>
                    <MDBModalDialog>
                        <MDBModalContent>
                            <MDBModalHeader>
                                <MDBModalTitle>Pay Photographer Slot</MDBModalTitle>
                                <MDBBtn floating onClick={() => { this.togglePayModal(); }} style={{ display: 'flex', padding: 5, height: 30, width: 30 }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="30" height="30" viewBox="0 0 30 30">
                                        <path d="M 7 4 C 6.744125 4 6.4879687 4.0974687 6.2929688 4.2929688 L 4.2929688 6.2929688 C 3.9019687 6.6839688 3.9019687 7.3170313 4.2929688 7.7070312 L 11.585938 15 L 4.2929688 22.292969 C 3.9019687 22.683969 3.9019687 23.317031 4.2929688 23.707031 L 6.2929688 25.707031 C 6.6839688 26.098031 7.3170313 26.098031 7.7070312 25.707031 L 15 18.414062 L 22.292969 25.707031 C 22.682969 26.098031 23.3170313 26.098031 23.7070312 25.707031 L 25.707031 23.707031 C 26.098031 23.316031 26.098031 22.682969 25.707031 22.292969 L 18.414062 15 L 25.707031 7.7070312 C 26.098031 7.3170312 26.098031 6.6829688 25.707031 6.2929688 L 23.707031 4.2929688 C 23.316031 3.9019687 22.682969 3.9019687 22.292969 4.2929688 L 15 11.585938 L 7.7070312 4.2929688 C 7.5115312 4.0974687 7.255875 4 7 4 z" stroke="#FFF" fill="#FFF"></path>
                                    </svg>
                                </MDBBtn>
                            </MDBModalHeader>
                            <MDBModalBody style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                {/* BUG-025: clearly distinguish live vs test payouts in the UI. */}
                                {this.state.payoutTestMode ? (
                                    <div style={{ background: '#FFF7E6', border: '1px solid #FFD591', color: '#AD6800', padding: '10px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600 }}>
                                        TEST PAYOUT MODE — no real money will move. Configure PAYOUT_TEST_MODE=false on the server to release live payouts.
                                    </div>
                                ) : (
                                    <div style={{ background: '#FFF1F0', border: '1px solid #FFA39E', color: '#A8071A', padding: '10px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600 }}>
                                        LIVE PAYOUT — this will transfer real funds via Razorpay. Double-check the hours, amount and bank details before confirming.
                                    </div>
                                )}
                                <div>
                                    <div style={{ fontSize: 13, color: '#999' }}>{this.state.selectedPayment?.order_receipt_id}</div>
                                    <div style={{ fontSize: 15 }}>{this.state.selectedPayment?.photographer_name}</div>
                                    <div style={{ fontSize: 13, color: '#999' }}>{this.state.selectedPayment?.specialization}</div>
                                </div>
                                <MDBInput
                                    label="Hours"
                                    type="number"
                                    value={this.state.payHours}
                                    onChange={(e: any) => this.setState({ payHours: e.target.value })}
                                />
                                <MDBInput
                                    label="Amount (from Services tab rate — editable)"
                                    type="number"
                                    value={this.state.payAmount}
                                    onChange={(e: any) => this.setState({ payAmount: e.target.value })}
                                />
                                {/* Client-reported: link photographer costing to the Services
                                    tab's payout rate and make it clear it's editable — this
                                    hint used to hide itself once it had already been used to
                                    prefill the field, which buried exactly the context that
                                    explains where the number came from. */}
                                {this.state.selectedPayment?.suggested_amount ? (
                                    <div style={{ fontSize: 12, color: '#999' }}>
                                        Services tab rate for this service/duration: ₹{this.state.selectedPayment.suggested_amount}. Edit the Amount field above to override.
                                    </div>
                                ) : null}
                                {/* BUG-025: require explicit confirmation for live payouts.
                                    Client-reported: replaced the typed "PAY LIVE" text field
                                    with a plain tick-mark checkbox. */}
                                {!this.state.payoutTestMode && (
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={this.state.liveConfirmChecked}
                                            onChange={(e) => this.setState({ liveConfirmChecked: e.target.checked })}
                                            style={{ width: 16, height: 16 }}
                                        />
                                        I confirm the hours and amount are correct and authorize this live payout.
                                    </label>
                                )}
                            </MDBModalBody>
                            <MDBModalFooter>
                                {/* Client-reported: payment should only go out through this one
                                    "Pay Using Bank" button — removed the separate mode-selector
                                    button (bank was always the only mode) and the "Close"/
                                    "Release Live Payout" buttons. The header's × still closes
                                    the modal without paying. */}
                                <MDBBtn
                                    color={this.state.payoutTestMode ? 'primary' : 'danger'}
                                    onClick={this.submitPayment}
                                    disabled={
                                        !(Number(this.state.payAmount) > 0) ||
                                        (!this.state.payoutTestMode && !this.state.liveConfirmChecked)
                                    }
                                >
                                    Pay Using Bank{this.state.payoutTestMode ? ' (Test)' : ''}
                                </MDBBtn>
                            </MDBModalFooter>
                        </MDBModalContent>
                    </MDBModalDialog>
                </MDBModal>
            </AdminShell>
        );
    }
}

const mapDispatchToProps = (dispatch: Function) => {
    return {
        dispatch
    }
}

const mapStateToProps = (state: any) => {
    return {
        token: state.auth.token,
        role: state.auth.role,
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(PaymentList);
